import React, { useCallback, useContext, useEffect, useState } from 'react';
import { Avatar, Button, Chip, FormControl, Grid, IconButton, InputAdornment, InputLabel, MenuItem, Paper, Select, TextField, Typography } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { Skeleton } from '@material-ui/lab';
import CalendarTodayOutlinedIcon from '@material-ui/icons/CalendarTodayOutlined';
import SearchIcon from '@material-ui/icons/Search';
import CloseIcon from '@material-ui/icons/Close';
import ReplayIcon from '@material-ui/icons/Replay';
import HistoryOutlinedIcon from '@material-ui/icons/HistoryOutlined';
import MUIDataTable from 'mui-datatables';
import { useSnackbar } from 'notistack';
import _ from 'lodash';
import moment from 'moment';
import AppContext from '../../Services/AppContext';
import PersistantData from '../../Services/PersistantData';
import API_Service, { axiosDefaultConfig, axiosURIencode } from '../../Services/API_Service';
import { req_audit_trail_outlet, req_audit_trail_outlet_users } from '../../Helper/Constants';
import { useCommonStyles } from '../../Styles';
import LinearLoading from '../../Components/LinearLoading';
import DateRangePickerModal from '../../Components/DateRangePickerModal';
import OutletAuditDetail, { FIELD_LABEL_KEYS } from './components/OutletAuditDetail';

const SYSTEM_AUTHOR = '__SYSTEM__';

const ACTION_OPTIONS = [
	{ value: 'created', labelKey: 'action_create' },
	{ value: 'updated', labelKey: 'action_update' },
	{ value: 'active', labelKey: 'action_active' },
	{ value: 'inactive', labelKey: 'action_inactive' },
	{ value: 'assigned', labelKey: 'action_assign' },
	{ value: 'unassigned', labelKey: 'action_unassign' }
];

const ACTION_CLASS_MAP = {
	created: 'chipCreated',
	updated: 'chipUpdated',
	active: 'chipActive',
	inactive: 'chipInactive',
	assigned: 'chipAssigned',
	unassigned: 'chipUnassigned'
};

const ACTION_DOT_MAP = {
	created: 'dotCreated',
	updated: 'dotUpdated',
	active: 'dotActive',
	inactive: 'dotInactive',
	assigned: 'dotAssigned',
	unassigned: 'dotUnassigned'
};

const ASSIGN_ACTIONS = ['assigned', 'unassigned'];

const useStyles = makeStyles((theme) => ({
	root: {
		padding: theme.spacing(1),
		overflow: 'hidden'
	},
	enter: {
		animation: '$riseIn 260ms cubic-bezier(0.4, 0, 0.2, 1) both'
	},
	'@keyframes riseIn': {
		from: {
			opacity: 0,
			transform: 'translateY(8px)'
		},
		to: {
			opacity: 1,
			transform: 'none'
		}
	},
	filterPaper: {
		padding: theme.spacing(2),
		marginBottom: theme.spacing(2),
		borderRadius: 10,
		border: `1px solid ${theme.palette.divider}`,
		backgroundColor: theme.palette.background.paper,
		overflow: 'hidden'
	},
	filterControl: {
		width: '100%'
	},
	dateBtn: {
		width: '100%',
		height: 40,
		textTransform: 'none',
		justifyContent: 'flex-start',
		overflow: 'hidden',
		borderColor: theme.palette.divider,
		transition: 'border-color 160ms ease, background-color 160ms ease',
		'&:hover': {
			borderColor: theme.palette.text.secondary
		}
	},
	dateBtnActive: {
		borderColor: theme.palette.primary.main,
		'&:hover': {
			borderColor: theme.palette.primary.main
		}
	},
	dateBtnText: {
		width: '100%',
		textAlign: 'left',
		overflow: 'hidden',
		textOverflow: 'ellipsis',
		whiteSpace: 'nowrap'
	},
	btnIcon: {
		fontSize: 16,
		color: theme.palette.text.secondary,
		transition: 'color 160ms ease'
	},
	btnIconActive: {
		color: theme.palette.primary.main
	},
	clearBtn: {
		padding: 2,
		marginRight: -4
	},
	clearIcon: {
		fontSize: 15
	},
	actionBtn: {
		width: '100%',
		height: 40,
		textTransform: 'none',
		borderColor: theme.palette.divider,
		transition: 'border-color 160ms ease, transform 120ms ease',
		'&:hover': {
			borderColor: theme.palette.text.secondary
		},
		'&:active': {
			transform: 'scale(0.985)'
		}
	},
	menuItem: {
		display: 'flex',
		alignItems: 'center'
	},
	dot: {
		display: 'inline-block',
		width: 8,
		height: 8,
		flexShrink: 0,
		borderRadius: '50%',
		marginRight: theme.spacing(1),
		backgroundColor: theme.palette.text.disabled
	},
	dotCreated: {
		backgroundColor: '#33c17a'
	},
	dotUpdated: {
		backgroundColor: '#4d9cf0'
	},
	dotActive: {
		backgroundColor: '#33c17a'
	},
	dotInactive: {
		backgroundColor: '#f0554d'
	},
	dotAssigned: {
		backgroundColor: '#26a69a'
	},
	dotUnassigned: {
		backgroundColor: '#ffa726'
	},
	tableArea: {
		position: 'relative',
		marginTop: theme.spacing(1)
	},
	loadingBar: {
		position: 'absolute',
		top: 1,
		left: 1,
		right: 1,
		zIndex: 3,
		borderTopLeftRadius: 10,
		borderTopRightRadius: 10,
		overflow: 'hidden'
	},
	tableWrapper: {
		transition: 'opacity 200ms ease',
		'& .MuiPaper-root': {
			borderRadius: 10,
			boxShadow: 'none',
			border: `1px solid ${theme.palette.divider}`,
			overflow: 'hidden'
		},
		'& .MuiTableCell-head': {
			fontSize: 11,
			fontWeight: 700,
			letterSpacing: 0.3,
			whiteSpace: 'nowrap',
			color: theme.palette.text.secondary,
			borderBottom: `1px solid ${theme.palette.divider}`
		},
		'& .MuiTableBody-root .MuiTableRow-hover': {
			transition: 'background-color 160ms ease, box-shadow 160ms ease'
		},
		'& .MuiTableBody-root .MuiTableRow-hover .MuiTableCell-root': {
			paddingTop: theme.spacing(1),
			paddingBottom: theme.spacing(1),
			borderBottom: `1px solid ${theme.palette.divider}`
		},
		'& .MuiTableBody-root .MuiTableRow-hover:hover': {
			backgroundColor: theme.palette.action.hover,
			boxShadow: `inset 3px 0 0 ${theme.palette.primary.main}`
		},
		'& .MuiTableBody-root .MuiTableRow-hover:hover $userAvatar': {
			transform: 'scale(1.08)'
		},
		'& .MuiTableBody-root $rowExpanded': {
			backgroundColor: theme.palette.action.hover
		},
		// 箭头格是 sticky 实心底色，会盖住行底色，叠一层同色才会连成一片
		'& .MuiTableBody-root $rowExpanded .MuiTableCell-paddingCheckbox': {
			backgroundImage: `linear-gradient(${theme.palette.action.hover}, ${theme.palette.action.hover})`
		},
		'& .MuiTableBody-root .MuiTableRow-hover$rowExpanded .MuiTableCell-root': {
			borderBottom: 'none'
		}
	},
	rowExpanded: {},
	tableDim: {
		opacity: 0.55,
		pointerEvents: 'none'
	},
	userCell: {
		display: 'flex',
		alignItems: 'center'
	},
	userAvatar: {
		width: 26,
		height: 26,
		fontSize: 11,
		fontWeight: 700,
		marginRight: theme.spacing(1),
		backgroundColor: '#FEF200',
		color: theme.palette.getContrastText('#FEF200'),
		transition: 'transform 180ms cubic-bezier(0.4, 0, 0.2, 1)'
	},
	userName: {
		fontWeight: 600,
		lineHeight: 1.2
	},
	userRole: {
		fontSize: 11,
		color: theme.palette.text.secondary,
		lineHeight: 1.2
	},
	outletChain: {
		fontSize: 11,
		color: theme.palette.text.secondary,
		lineHeight: 1.2
	},
	timeSecondary: {
		fontSize: 11,
		color: theme.palette.text.secondary,
		lineHeight: 1.2
	},
	chip: {
		height: 24,
		fontSize: 11,
		fontWeight: 600,
		borderRadius: 6
	},
	chipCreated: {
		backgroundColor: 'rgba(51, 193, 122, 0.15)',
		color: '#33c17a'
	},
	chipUpdated: {
		backgroundColor: 'rgba(77, 156, 240, 0.15)',
		color: '#4d9cf0'
	},
	chipActive: {
		backgroundColor: 'rgba(51, 193, 122, 0.15)',
		color: '#33c17a'
	},
	chipInactive: {
		backgroundColor: 'rgba(240, 85, 77, 0.15)',
		color: '#f0554d'
	},
	chipAssigned: {
		backgroundColor: 'rgba(38, 166, 154, 0.15)',
		color: '#26a69a'
	},
	chipUnassigned: {
		backgroundColor: 'rgba(255, 167, 38, 0.15)',
		color: '#ffa726'
	},
	descText: {
		maxWidth: 260
	},
	ipText: {
		fontFamily: 'Menlo, Consolas, monospace',
		fontSize: 12,
		color: theme.palette.text.secondary
	},
	emptyWrap: {
		display: 'flex',
		flexDirection: 'column',
		alignItems: 'center',
		justifyContent: 'center',
		padding: theme.spacing(5, 2)
	},
	emptyIcon: {
		fontSize: 38,
		color: theme.palette.text.disabled,
		marginBottom: theme.spacing(1)
	},
	emptyTitle: {
		fontWeight: 600
	},
	emptyHint: {
		marginTop: theme.spacing(0.5),
		color: theme.palette.text.secondary
	},
	skeletonFilter: {
		marginBottom: theme.spacing(2),
		borderRadius: 10
	},
	skeletonTable: {
		borderRadius: 10
	},
	'@media (prefers-reduced-motion: reduce)': {
		enter: {
			animation: 'none'
		},
		userAvatar: {
			transition: 'none'
		},
		tableWrapper: {
			transition: 'none'
		}
	}
}));

export default function AuditTrail() {
	const classes = useStyles();
	const commonStyle = useCommonStyles();
	const singleton = PersistantData.getInstance();
	const [gState] = useContext(AppContext);
	var { translation: language, client } = gState;
	if (typeof (language) === 'undefined') {
		language = singleton.getlang();
	}

	const { enqueueSnackbar } = useSnackbar();

	const [loading, setLoading] = useState(true);
	const [tableLoading, setTableLoading] = useState(false);
	const [auditList, setAuditList] = useState([]);
	const [totalCount, setTotalCount] = useState(0);
	const [userList, setUserList] = useState([]);
	const [selectedAction, setSelectedAction] = useState('');
	const [selectedUser, setSelectedUser] = useState('');
	const [searchText, setSearchText] = useState('');
	const [dateRange, setDateRange] = useState({});
	const [openDatePicker, setOpenDatePicker] = useState(false);
	const [page, setPage] = useState(0);
	const [rowsPerPage, setRowsPerPage] = useState(20);
	// mui-datatables 2.14 不吃 options.page，换筛选条件要重挂表格才能把分页归零
	const [tableKey, setTableKey] = useState(0);
	// 展开中的 dataIndex；columns 每次 render 都是新的，表格会按 rowsExpanded 重建展开状态
	const [expandedRows, setExpandedRows] = useState([]);

	const snack = (msg, type = 'success') => {
		enqueueSnackbar(msg, {
			variant: type,
			preventDuplicate: true,
			autoHideDuration: type === 'success' ? 2000 : 3000
		});
	};

	const loadAuditTrail = useCallback((override = {}) => {
		const nextPage = override.page !== undefined ? override.page : page;
		const nextLimit = override.limit !== undefined ? override.limit : rowsPerPage;
		const nextAction = override.action !== undefined ? override.action : selectedAction;
		const nextUser = override.author !== undefined ? override.author : selectedUser;
		const nextSearch = override.search !== undefined ? override.search : searchText;
		const nextRange = override.range !== undefined ? override.range : dateRange;

		const hasRange = !_.isEmpty(nextRange) && nextRange.startDate && nextRange.endDate;

		const params = axiosURIencode({
			date_from: hasRange ? moment(nextRange.startDate).format('YYYY-MM-DD') : '',
			date_to: hasRange ? moment(nextRange.endDate).format('YYYY-MM-DD') : '',
			action: nextAction,
			author: nextUser,
			search: nextSearch,
			page: nextPage + 1,
			limit: nextLimit
		});

		setTableLoading(true);
		setExpandedRows([]);
		API_Service.get(`${req_audit_trail_outlet}?${params}`, axiosDefaultConfig(client)).then(({ data }) => {
			const { result, message, extraData, total_count } = data;
			if (parseInt(result) === 1) {
				setAuditList(extraData);
				setTotalCount(parseInt(total_count || 0, 10));
			} else {
				setAuditList([]);
				setTotalCount(0);
				if (message && message !== 'No Data Found') {
					snack(message, 'error');
				}
			}
			setTableLoading(false);
			setLoading(false);
		}).catch(() => {
			setAuditList([]);
			setTotalCount(0);
			setTableLoading(false);
			setLoading(false);
			snack(language.login_network_error, 'error');
		});
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [client, page, rowsPerPage, selectedAction, selectedUser, searchText, dateRange]);

	const loadAuditUsers = useCallback(() => {
		API_Service.get(req_audit_trail_outlet_users, axiosDefaultConfig(client)).then(({ data }) => {
			const { result, extraData } = data;
			if (parseInt(result) === 1) {
				setUserList(extraData);
			} else {
				setUserList([]);
			}
		}).catch(() => snack(language.login_network_error, 'error'));
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [client]);

	useEffect(() => {
		if (client) {
			loadAuditUsers();
			loadAuditTrail({ page: 0 });
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [client]);

	const handleActionChange = (e) => {
		const value = e.target.value;
		setSelectedAction(value);
		setPage(0);
		setTableKey((prev) => prev + 1);
		loadAuditTrail({ action: value, page: 0 });
	};

	const handleUserChange = (e) => {
		const value = e.target.value;
		setSelectedUser(value);
		setPage(0);
		setTableKey((prev) => prev + 1);
		loadAuditTrail({ author: value, page: 0 });
	};

	const handleSearchKeyPress = (e) => {
		if (e.key === 'Enter') {
			setPage(0);
			setTableKey((prev) => prev + 1);
			loadAuditTrail({ search: e.target.value, page: 0 });
		}
	};

	const handleSearchClear = () => {
		setSearchText('');
		setPage(0);
		setTableKey((prev) => prev + 1);
		loadAuditTrail({ search: '', page: 0 });
	};

	const handleDateConfirm = (range) => {
		setOpenDatePicker(false);
		if (_.isEmpty(range) || !range.startDate || !range.endDate) {
			return;
		}
		setDateRange({ ...range, key: 'selection' });
		setPage(0);
		setTableKey((prev) => prev + 1);
		loadAuditTrail({ range: range, page: 0 });
	};

	const handleReset = () => {
		setSelectedAction('');
		setSelectedUser('');
		setSearchText('');
		setDateRange({});
		setPage(0);
		setTableKey((prev) => prev + 1);
		loadAuditTrail({ action: '', author: '', search: '', range: {}, page: 0 });
	};

	const hasDateRange = !_.isEmpty(dateRange) && dateRange.startDate && dateRange.endDate;

	const getDateDisplay = () => {
		if (!hasDateRange) {
			return language.date_range;
		}
		return `${moment(dateRange.startDate).format('DD MMM YYYY')} / ${moment(dateRange.endDate).format('DD MMM YYYY')}`;
	};

	const userLabel = (user) => {
		if (user.author_name === SYSTEM_AUTHOR) {
			return language.audit_system_user;
		}
		return user.author_display_name || user.author_name;
	};

	const describeRow = (row) => {
		const actionOption = _.find(ACTION_OPTIONS, (o) => o.value === row.remark);
		const actionText = actionOption ? language[actionOption.labelKey] : row.remark;
		if (ASSIGN_ACTIONS.includes(row.remark)) {
			const change = _.find(row.changes || [], (c) => c.field === 'assigned_user');
			const user = change ? (row.remark === 'assigned' ? change.new : change.old) : '';
			return user ? `${actionText}: ${user}` : actionText;
		}
		if (row.remark !== 'updated') {
			return actionText;
		}
		const fields = (row.changes || []).map((change) => {
			const key = FIELD_LABEL_KEYS[change.field];
			return key && language[key] ? language[key] : change.field;
		});
		if (fields.length === 0) {
			return actionText;
		}
		return `${actionText}: ${fields.join(', ')}`;
	};

	const columns = [
		{
			name: 'created_at',
			label: language.timestamp,
			options: {
				sort: false,
				customBodyRender: (value) => {
					const m = moment(value, 'YYYY-MM-DD HH:mm:ss');
					return (
						<div>
							<Typography variant={'body2'}>{m.format('DD/MM/YYYY')}</Typography>
							<Typography className={classes.timeSecondary}>{m.format('HH:mm:ss')}</Typography>
						</div>
					);
				}
			}
		},
		{
			name: 'author_name',
			label: language.performed_by,
			options: {
				sort: false,
				customBodyRender: (value, tableMeta) => {
					const row = auditList[tableMeta.rowIndex];
					if (!row) {
						return null;
					}
					const name = row.author_display_name || value || language.audit_system_user;
					const role = row.author_role || (parseInt(row.author_is_admin, 10) === 1 ? language.role_admin : '-');
					return (
						<div className={classes.userCell}>
							<Avatar className={classes.userAvatar}>
								{String(name).charAt(0).toUpperCase()}
							</Avatar>
							<div>
								<Typography variant={'body2'} className={classes.userName}>{name}</Typography>
								<Typography className={classes.userRole}>{role}</Typography>
							</div>
						</div>
					);
				}
			}
		},
		{
			name: 'cust_company_name',
			label: language.customer_low,
			options: {
				sort: false,
				customBodyRender: (value, tableMeta) => {
					const row = auditList[tableMeta.rowIndex];
					if (!row) {
						return null;
					}
					return (
						<div>
							<Typography variant={'body2'}>{value || row.cust_code || '-'}</Typography>
							<Typography className={classes.outletChain}>{row.cust_chain_name || '-'}</Typography>
						</div>
					);
				}
			}
		},
		{
			name: 'remark',
			label: language.action,
			options: {
				sort: false,
				customBodyRender: (value) => {
					const actionOption = _.find(ACTION_OPTIONS, (o) => o.value === value);
					const chipClass = ACTION_CLASS_MAP[value];
					return (
						<Chip
							size={'small'}
							label={actionOption ? language[actionOption.labelKey] : value}
							className={`${classes.chip} ${chipClass ? classes[chipClass] : ''}`}
						/>
					);
				}
			}
		},
		{
			name: 'id',
			label: language.description,
			options: {
				sort: false,
				customBodyRender: (value, tableMeta) => {
					const row = auditList[tableMeta.rowIndex];
					if (!row) {
						return null;
					}
					return (
						<Typography variant={'body2'} className={classes.descText}>
							{_.truncate(describeRow(row), { length: 80 })}
						</Typography>
					);
				}
			}
		},
		{
			name: 'ip_address',
			label: language.ip_address,
			options: {
				sort: false,
				customBodyRender: (value) => (
					<Typography variant={'body2'} className={classes.ipText}>{value || '-'}</Typography>
				)
			}
		}
	];

	const emptyState = (
		<span className={classes.emptyWrap}>
			<HistoryOutlinedIcon className={classes.emptyIcon} />
			<Typography variant={'body2'} component={'span'} className={classes.emptyTitle}>
				{language.no_matching_events}
			</Typography>
			<Typography variant={'caption'} component={'span'} className={classes.emptyHint}>
				{language.no_matching_events_hint}
			</Typography>
		</span>
	);

	const options = {
		selectableRows: 'none',
		responsive: 'standard',
		rowsPerPage: rowsPerPage,
		rowsPerPageOptions: [20, 50, 100],
		download: false,
		print: false,
		filter: false,
		search: false,
		sort: false,
		serverSide: true,
		count: totalCount,
		page: page,
		expandableRows: true,
		expandableRowsOnClick: true,
		rowsExpanded: expandedRows,
		onRowsExpand: (currentRowsExpanded, allRowsExpanded) => {
			setExpandedRows(allRowsExpanded.map((r) => r.dataIndex));
		},
		setRowProps: (rowData, dataIndex) => (
			expandedRows.includes(dataIndex) ? { className: classes.rowExpanded } : {}
		),
		textLabels: {
			body: {
				noMatch: emptyState
			}
		},
		renderExpandableRow: (rowData, rowMeta) => {
			const row = auditList[rowMeta.dataIndex];
			if (!row) {
				return null;
			}
			return <OutletAuditDetail row={row} colSpan={rowData.length + 1} />;
		},
		onTableChange: (action, tableState) => {
			if (action === 'changePage') {
				setPage(tableState.page);
				loadAuditTrail({ page: tableState.page });
			}
			if (action === 'changeRowsPerPage') {
				setRowsPerPage(tableState.rowsPerPage);
				setPage(0);
				loadAuditTrail({ limit: tableState.rowsPerPage, page: 0 });
			}
		},
		setTableProps: () => ({ size: 'small' })
	};

	if (loading) {
		return (
			<div className={classes.root}>
				<Skeleton animation={'wave'} height={90} className={classes.skeletonFilter} />
				<Skeleton animation={'wave'} variant={'rect'} height={360} className={classes.skeletonTable} />
			</div>
		);
	}

	return (
		<div className={`${classes.root} ${classes.enter}`}>
			<Paper variant={'outlined'} className={classes.filterPaper}>
				<Grid container spacing={1} alignItems={'center'}>
					<Grid item xs={12} sm={3}>
						<Button
							className={`${classes.dateBtn} ${hasDateRange ? classes.dateBtnActive : ''}`}
							variant={'outlined'}
							size={'small'}
							startIcon={
								<CalendarTodayOutlinedIcon
									className={`${classes.btnIcon} ${hasDateRange ? classes.btnIconActive : ''}`}
								/>
							}
							onClick={() => setOpenDatePicker(true)}>
							<Typography className={`${commonStyle.searchBtnFont} ${classes.dateBtnText}`}>
								{getDateDisplay()}
							</Typography>
						</Button>
					</Grid>

					<Grid item xs={12} sm={2}>
						<FormControl variant={'outlined'} size={'small'} className={classes.filterControl}>
							<InputLabel id={'audit-action-label'}>{language.action}</InputLabel>
							<Select
								labelId={'audit-action-label'}
								label={language.action}
								value={selectedAction}
								onChange={handleActionChange}>
								<MenuItem value={''} className={classes.menuItem}>
									<span className={classes.dot} />
									{language.all_actions}
								</MenuItem>
								{
									ACTION_OPTIONS.map((option) => (
										<MenuItem key={option.value} value={option.value} className={classes.menuItem}>
											<span className={`${classes.dot} ${classes[ACTION_DOT_MAP[option.value]]}`} />
											{language[option.labelKey]}
										</MenuItem>
									))
								}
							</Select>
						</FormControl>
					</Grid>

					<Grid item xs={12} sm={2}>
						<FormControl variant={'outlined'} size={'small'} className={classes.filterControl}>
							<InputLabel id={'audit-user-label'}>{language.performed_by}</InputLabel>
							<Select
								labelId={'audit-user-label'}
								label={language.performed_by}
								value={selectedUser}
								onChange={handleUserChange}>
								<MenuItem value={''}>{language.all_users}</MenuItem>
								{
									userList.map((user) => (
										<MenuItem key={user.author_name} value={user.author_name}>
											{userLabel(user)}
										</MenuItem>
									))
								}
							</Select>
						</FormControl>
					</Grid>

					<Grid item xs={12} sm={3}>
						<TextField
							className={classes.filterControl}
							variant={'outlined'}
							size={'small'}
							label={language.search}
							value={searchText}
							onChange={(e) => setSearchText(e.target.value)}
							onKeyPress={handleSearchKeyPress}
							InputProps={{
								startAdornment: (
									<InputAdornment position={'start'}>
										<SearchIcon className={classes.btnIcon} />
									</InputAdornment>
								),
								endAdornment: searchText ? (
									<InputAdornment position={'end'}>
										<IconButton size={'small'} className={classes.clearBtn} onClick={handleSearchClear}>
											<CloseIcon className={classes.clearIcon} />
										</IconButton>
									</InputAdornment>
								) : null
							}}
						/>
					</Grid>

					<Grid item xs={12} sm={2}>
						<Button
							className={classes.actionBtn}
							size={'small'}
							variant={'outlined'}
							startIcon={<ReplayIcon className={classes.btnIcon} />}
							onClick={handleReset}>
							{language.reset}
						</Button>
					</Grid>
				</Grid>
			</Paper>

			<div className={classes.tableArea}>
				{
					tableLoading &&
					<div className={classes.loadingBar}>
						<LinearLoading />
					</div>
				}
				<div className={`${classes.tableWrapper} ${tableLoading ? classes.tableDim : ''}`}>
					<MUIDataTable
						key={tableKey}
						title={language.audit_trail}
						data={auditList}
						columns={columns}
						options={options}
					/>
				</div>
			</div>

			<DateRangePickerModal
				setPositive={handleDateConfirm}
				setNegative={() => setOpenDatePicker(false)}
				setClose={() => setOpenDatePicker(false)}
				open={openDatePicker}
				input={dateRange}
				message={language.date_range}
				positive={language.ok}
				negative={language.cancel}
			/>
		</div>
	);
}
